import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { getRecentTransactionsAction } from "../../Actions/TransactionActions";
import { DataGrid_SX } from "../../Assets/customTheme/constants";
import { renderStatusBadge } from "../../utils/badges";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";

const DashboardRecentTransactions = ({
  showButton,
  onRemove,
  hasTransactionPermission,
  onShowSnackbar,
}) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch recent transactions
  const fetchRecentTransactions = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getRecentTransactionsAction();
      setTransactions(response || []);
    } catch (error) {
      setTransactions([]);
      if (error?.response?.data?.message) {
        onShowSnackbar(error.response.data.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [onShowSnackbar]);

  useEffect(() => {
    fetchRecentTransactions();
  }, [fetchRecentTransactions]);

  const columns = useMemo(() => [
    {
      field: "merchantName",
      headerName: "Tenant Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.row.merchantDetails
          ? `${params.row.merchantDetails.firstName} ${params.row.merchantDetails.lastName}`
          : "Unknown",
    },
    {
      field: "transactionId",
      headerName: "Transaction ID",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "companyName",
      headerName: "Company Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.row.merchantDetails ? params.row.merchantDetails.companyName : "N/A",
    },
    {
      field: "createdAt",
      headerName: "Date & Time",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.row.createdAt
          ? new Date(params.row.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
            })
          : "N/A",
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => `CI$ ${parseFloat(params.row.amount || 0).toFixed(2)}`,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const statusMap = {
          0: { text: "Pending", status: "warning" },
          1: { text: "Success", status: "success" },
          2: { text: "Failed", status: "failed" },
        };
        const statusCode = params.row.status ?? "0";
        const { text, status } = statusMap[statusCode] || {
          text: "Unknown",
          status: "default",
        };
        return renderStatusBadge({ status, title: text });
      },
    },
  ], []);

  if (!hasTransactionPermission?.canView) return null;

  return (
    <div key="recentTransactionList">
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("recentTransactionList")}
        />
      )}

      <div className="card border-rounded-2 fiter-card">
        <div className="card-body p-3 d-flex justify-content-between align-items-center">
          <h2 className="chart-title mb-0">Recent Transactions</h2>
          <button
            className="view-all"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/transactions")}
          >
            View All
          </button>
        </div>
      </div>

      <DataGrid
        localeText={{ toolbarColumns: "" }}
        rows={transactions || []}
        columns={columns}
        autoHeight
        hideFooterRowCount={true}
        hideFooterPagination={true}
        hideFooter={true}
        loading={loading}
        components={{
          loadingOverlay: () => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "110px",
              }}
            >
              <CircularProgress
                color="primary"
                size={24}
                style={{
                  marginRight: 10,
                }}
              />
              Loading transactions...
            </div>
          ),
          noRowsOverlay: () =>
            !loading && transactions.length === 0 ? (
              <div
                style={{
                  fontWeight: "bold",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                No Records Found
              </div>
            ) : null,
        }}
        sx={DataGrid_SX}
      />
    </div>
  );
};

export default DashboardRecentTransactions;